import { KdsModal, KdsText, VStack } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import { useOrderItems, useToasts } from "@/hooks";
import { type ModalCancelProductSelectionProps } from "./ModalCancelProductSelection.types";

export const ModalCancelProductSelection = ({
  isOpen,
  selectedOrderItems,
  onOpenChange,
}: ModalCancelProductSelectionProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const navigate = useNavigate();

  const { useCancelOrderItem } = useOrderItems();
  const { mutateAsync: cancelOrderItem, isPending } = useCancelOrderItem();

  const handleSubmit = async () => {
    try {
      await Promise.all(
        selectedOrderItems.map((orderItem) =>
          cancelOrderItem({
            orderItemId: orderItem.orderItemId,
            orderId: orderItem.orderId || "",
          })
        )
      );
      add({
        state: "success",
        title: t("expertMode.ModalCancelProductSelection.toast.success"),
      });
      onOpenChange();

      void navigate(`/orders/${selectedOrderItems[0].orderId || ""}`);
    } catch (error) {
      console.error(error);
      add({
        state: "error",
        title: t("expertMode.ModalCancelProductSelection.toast.error"),
      });
    }
  };

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          title: t("expertMode.ModalCancelProductSelection.title"),
        },
        content: (
          <VStack gap="sp-md">
            <KdsText size="subtitle-md">
              {t("expertMode.ModalCancelProductSelection.description")}
            </KdsText>
            <VStack>
              {selectedOrderItems.map((orderItem) => (
                <KdsText key={orderItem.orderItemId} size="subtitle-md">
                  • {orderItem.name}
                </KdsText>
              ))}
            </VStack>
            <KdsText>
              {t("expertMode.ModalCancelProductSelection.notifiedByEmail")}
            </KdsText>
          </VStack>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.cancel"),
              variant: "ghost",
              color: "neutral",
              isLoading: isPending,
              disabled: isPending,
              onPress: onOpenChange,
            },
            {
              disabled: isPending,
              isLoading: isPending,
              color: "error",
              children: t("buttons.delete"),
              onPress: handleSubmit,
            },
          ],
        },
      }}
    />
  );
};
